"use client";

import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HiArrowRight, HiArrowLeft, HiCheck } from "@/components/icons";
import { useToast } from "@/hooks/use-toast";
import Link from "next/link";
import { isValidEmail } from "@/lib/utils";
import { FORM_SERVICES, ECOMMERCE_DEFAULTS } from "@/lib/analytics/config";
import { trackLeadSubmission, trackFormInteraction } from "@/lib/analytics/events";

interface FunnelData {
  projectType: string;
  features: string[];
  budget: string;
  timeline: string;
  email: string;
  phone: string;
  details: string;
}

const ANALYTICS_FORM_ID = "custom_extension_quote";

/**
 * Lowest USD value of each budget band.
 *
 * GA4's generate_lead wants a numeric `value`; the label alone ("$2.5k - $5k
 * (MVP)") cannot be aggregated. This is the floor of the band the visitor
 * picked, which is the most defensible number: it never claims revenue we did
 * not quote, and it still lets lead ROAS be computed instead of guessed.
 */
const BUDGET_FLOORS: Readonly<Record<string, number>> = {
  "$2.5k - $5k (MVP)": 2500,
  "$5k - $10k (Standard)": 5000,
  "$10k - $25k (Advanced)": 10000,
  "$25k+ (Enterprise)": 25000,
};

interface StepProps {
  data: FunnelData;
  handleSelect: (key: keyof FunnelData, value: string) => void;
  handleMultiSelect: (key: keyof FunnelData, value: string) => void;
  nextStep: () => void;
  submit: () => void;
  isSubmitting: boolean;
}

const Step1_Type = ({ data, handleSelect }: Pick<StepProps, "data" | "handleSelect">) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <h3 className="text-2xl font-bold tracking-tight">What are we building?</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
        "Internal Workflow Tool",
        "SaaS Product / MVP",
        "Data Scraper / Crawler",
        "Social Media Automation",
        "Browser Security Tool",
        "Something Else",
      ].map((opt) => (
        <button
          key={opt}
          onClick={() => handleSelect("projectType", opt)}
          className={`p-4 text-left border rounded-lg transition-all hover:border-primary hover:bg-primary/5 ${
            data.projectType === opt
              ? "border-primary bg-primary/5 ring-1 ring-primary"
              : "border-border"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const Step2_Features = ({
  data,
  handleMultiSelect,
  nextStep,
}: Pick<StepProps, "data" | "handleMultiSelect" | "nextStep">) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <h3 className="text-2xl font-bold tracking-tight">Core Features Needed</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
        "User Accounts / Auth",
        "Payment Integration",
        "External API Connection",
        "DOM Manipulation",
        "Background Jobs",
        "Complex UI/Dashboard",
      ].map((opt) => (
        <button
          key={opt}
          onClick={() => handleMultiSelect("features", opt)}
          className={`p-4 text-left border rounded-lg transition-all flex justify-between items-center ${
            data.features.includes(opt)
              ? "border-primary bg-primary/5 ring-1 ring-primary"
              : "border-border hover:border-primary/50"
          }`}
        >
          <span>{opt}</span>
          {data.features.includes(opt) && <HiCheck className="text-primary" />}
        </button>
      ))}
    </div>
    <Button
      onClick={nextStep}
      className="w-full mt-4 rounded-full h-12 text-lg"
      disabled={data.features.length === 0}
    >
      Continue <HiArrowRight className="ml-2" />
    </Button>
  </div>
);

const Step3_Budget = ({ data, handleSelect }: Pick<StepProps, "data" | "handleSelect">) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <h3 className="text-2xl font-bold tracking-tight">Investment Range</h3>
    <p className="text-muted-foreground text-sm">
      Realistic budgets help us determine the stack and scope.
    </p>
    <div className="grid grid-cols-1 gap-3">
      {[
        "$2.5k - $5k (MVP)",
        "$5k - $10k (Standard)",
        "$10k - $25k (Advanced)",
        "$25k+ (Enterprise)",
      ].map((opt) => (
        <button
          key={opt}
          onClick={() => handleSelect("budget", opt)}
          className={`p-4 text-left border rounded-lg transition-all hover:border-primary hover:bg-primary/5 ${
            data.budget === opt
              ? "border-primary bg-primary/5 ring-1 ring-primary"
              : "border-border"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const Step4_Details = ({
  data,
  setData,
  submit,
  isSubmitting,
  emailError,
  onEmailChange,
  onPhoneChange,
}: {
  data: FunnelData;
  setData: (data: FunnelData) => void;
  submit: () => void;
  isSubmitting: boolean;
  emailError?: string;
  onEmailChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhoneChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <h3 className="text-2xl font-bold tracking-tight">The Fine Print</h3>
    <div className="space-y-4">
      <div>
        <Label htmlFor="funnel-email" className="text-base">
          Your Email
        </Label>
        <Input
          id="funnel-email"
          name="email"
          type="email"
          autoComplete="email"
          value={data.email}
          onChange={onEmailChange || ((e) => setData({ ...data, email: e.target.value }))}
          placeholder="you@company.com"
          className={`mt-2 h-14 rounded-xl border-2 focus:border-primary/20 ${emailError ? "border-red-500" : ""}`}
        />
        {emailError && <p className="text-sm text-red-500 mt-1">{emailError}</p>}
      </div>
      <div>
        <Label htmlFor="funnel-phone" className="text-base">
          Phone Number (Optional)
        </Label>
        <Input
          id="funnel-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          value={data.phone}
          onChange={onPhoneChange}
          placeholder="+1 (555) 000-0000"
          className="mt-2 h-14 rounded-xl border-2 focus:border-primary/20"
        />
      </div>
      <div>
        <Label htmlFor="funnel-details" className="text-base">
          Project Details (Optional)
        </Label>
        <Textarea
          id="funnel-details"
          name="details"
          value={data.details}
          onChange={(e) => setData({ ...data, details: e.target.value })}
          placeholder="Briefly describe the problem..."
          className="mt-2 rounded-xl border-2 focus:border-primary/20"
          rows={3}
        />
      </div>
    </div>
    <Button
      onClick={submit}
      disabled={!data.email || isSubmitting}
      className="w-full h-14 rounded-full text-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20"
    >
      {isSubmitting ? "Sending..." : "Get Quote"}
    </Button>
  </div>
);

const Step_Success = ({ email }: { email: string }) => (
  <div className="text-center space-y-6 py-12 animate-in fade-in zoom-in duration-500">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
      <HiCheck className="w-8 h-8" />
    </div>
    <h3 className="text-3xl font-bold">Received.</h3>
    <p className="text-muted-foreground max-w-md mx-auto">
      We'll analyze your request and send a preliminary technical breakdown to{" "}
      <strong>{email}</strong> within 24 hours.
    </p>
    <Link href="/">
      <Button variant="outline" className="mt-8">
        Back to Home
      </Button>
    </Link>
  </div>
);

export default function ModernFunnel() {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [data, setData] = useState<FunnelData>({
    projectType: "",
    features: [],
    budget: "",
    timeline: "",
    email: "",
    phone: "",
    details: "",
  });

  // The funnel is a 5-step wizard, so "first field focus" is not a literal
  // onFocus here — the first meaningful click or keystroke anywhere in it is
  // the real start of the interaction. A ref keeps it to one event per mount.
  const formStarted = useRef(false);

  const markFormStarted = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackFormInteraction("start", {
      form_id: ANALYTICS_FORM_ID,
      form_name: ANALYTICS_FORM_ID,
      form_source: "custom_extension_funnel",
    });
  };

  const handleSelect = (key: keyof FunnelData, value: string) => {
    markFormStarted();
    setData({ ...data, [key]: value });
    if (step < 5) setTimeout(() => setStep(step + 1), 200); // Auto advance for single selections
  };

  const handleMultiSelect = (key: keyof FunnelData, value: string) => {
    markFormStarted();
    const current = data[key] as string[];
    const updated = current.includes(value)
      ? current.filter((i) => i !== value)
      : [...current, value];
    setData({ ...data, [key]: updated });
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    markFormStarted();
    setData({ ...data, email: e.target.value });
    if (emailError) setEmailError("");
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    markFormStarted();
    const value = e.target.value;
    // Allow only numbers and typical phone characters like +, -, (, ), space
    if (/^[0-9+\-() ]*$/.test(value)) {
      setData({ ...data, phone: value });
    }
  };

  const submit = async () => {
    if (!isValidEmail(data.email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: FORM_SERVICES.WEB3FORMS_ACCESS_KEY,
          subject: "New PlugMonkey Lead (Custom Extension Quote)",
          from_name: "PlugMonkey Website",
          ...data,
          features: data.features.join(", "),
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error("Form submission failed");
      }
      setStep(6); // Success

      trackFormInteraction("submit", {
        form_id: ANALYTICS_FORM_ID,
        form_name: ANALYTICS_FORM_ID,
        form_source: "custom_extension_funnel",
      });

      trackLeadSubmission({
        form_name: ANALYTICS_FORM_ID,
        form_id: ANALYTICS_FORM_ID,
        form_source: "custom_extension_funnel",
        budget_range: data.budget,
        budget_value: BUDGET_FLOORS[data.budget],
        currency: ECOMMERCE_DEFAULTS.CURRENCY,
        lead_source: "custom_extension_page",
        // Sent to Google only as a SHA-256 hash (lib/analytics/user-data.ts)
        email: data.email,
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "Failed to send request. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-card/80 backdrop-blur-md border border-border/50 shadow-2xl rounded-[2.5rem] p-8 md:p-12 relative overflow-hidden">
      {/* Progress Bar */}
      {step < 6 && (
        <div className="absolute top-0 left-0 w-full h-1.5 bg-secondary">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out shadow-[0_0_10px_rgba(255,140,0,0.5)]"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>
      )}

      {step > 1 && step < 6 && (
        <button
          onClick={prevStep}
          className="absolute top-6 left-6 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Go back to previous step"
        >
          <HiArrowLeft />
        </button>
      )}

      <div className="mt-6">
        {step === 1 && <Step1_Type data={data} handleSelect={handleSelect} />}
        {step === 2 && (
          <Step2_Features data={data} handleMultiSelect={handleMultiSelect} nextStep={nextStep} />
        )}
        {step === 3 && <Step3_Budget data={data} handleSelect={handleSelect} />}
        {step === 4 && (
          <Step4_Details
            data={data}
            setData={setData}
            submit={submit}
            isSubmitting={isSubmitting}
            emailError={emailError}
            onEmailChange={handleEmailChange}
            onPhoneChange={handlePhoneChange}
          />
        )}
        {step === 5 && (
          <Step4_Details
            data={data}
            setData={setData}
            submit={submit}
            isSubmitting={isSubmitting}
            emailError={emailError}
            onEmailChange={handleEmailChange}
            onPhoneChange={handlePhoneChange}
          />
        )}{" "}
        {/* Redundant step for safety/index matching if needed */}
        {step === 6 && <Step_Success email={data.email} />}
      </div>

      {step < 5 && (
        <div className="mt-8 flex justify-center">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  step === i ? "bg-primary w-6" : "bg-secondary"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
